import $ from './el.JsModules.js';

import 'https://cdn.jsdelivr.net/npm/dayjs@1/dayjs.min.js';
import 'https://cdn.jsdelivr.net/npm/dayjs@1/plugin/utc.js';
import 'https://cdn.jsdelivr.net/npm/dayjs@1/plugin/timezone.js';
import 'https://cdn.jsdelivr.net/npm/dayjs@1/plugin/customParseFormat.js';
import 'https://cdn.jsdelivr.net/npm/dayjs@1/plugin/isBetween.js';

window.dayjs.extend(window.dayjs_plugin_utc);
window.dayjs.extend(window.dayjs_plugin_timezone);
window.dayjs.extend(window.dayjs_plugin_customParseFormat);
window.dayjs.extend(window.dayjs_plugin_isBetween);

export const dayjs = window.dayjs;

elabs.DeliveryCalculator = class {
  constructor(element) {
    this.$element = $(element);
    this.config = this.$element.data();
    this.selectors = {
      messageContainer: '#delivery-message',
      countdown: '#delivery-countdown',
    };
    this.testMode = false;
    this.testDate = null;
    this.init();
  }

  init() {
    this.parseConfig();
    this.cacheElements();
    this.updateMessage();
    this.startAutoUpdate();
  }

  parseConfig() {
    try {
      this.cutoffTime = this.config.cutoff.split(':').map(Number);
      this.closedDates = this.parseClosedDates();
      this.timezone = this.config.tz || 'Europe/London';
      this.maxLookaheadDays = 14;
    } catch (error) {
      console.error('Config error:', error);
    }
  }

  cacheElements() {
    this.$message = $(this.selectors.messageContainer);
    this.$countdown = $(this.selectors.countdown);
  }

  parseClosedDates() {
    return (this.config.closedDates?.split(',') || [])
      .filter((date) => date.trim())
      .map((date) => {
        if (date.includes('-')) {
          const [start, end] = date.split('-').map((d) => dayjs(d, 'DD/MM/YYYY', this.timezone).startOf('day'));
          return { start, end: end.endOf('day') };
        }
        const d = dayjs(date, 'DD/MM/YYYY', this.timezone);
        return { start: d.startOf('day'), end: d.endOf('day') };
      });
  }

  getCurrentDate() {
    return this.testMode ? this.testDate : dayjs().tz(this.timezone);
  }

  setTestDate(date, time) {
    if (!date) {
      this.testMode = false;
      this.testDate = null;
      this.updateMessage();
      return;
    }

    this.testMode = true;
    const datetimeStr = typeof date === 'string' ? (time ? `${date}T${time}` : date) : date.toISOString();

    this.testDate = dayjs.tz(datetimeStr, this.timezone);
    this.updateMessage();
  }

  isDateAvailable(date) {
    return (
      date.day() !== 0 &&
      date.day() !== 6 &&
      !this.closedDates.some(({ start, end }) => date.isBetween(start, end, null, '[]'))
    );
  }

  getNextAvailableDate(fromDate) {
    let date = fromDate.clone();
    for (let i = 0; i < this.maxLookaheadDays; i++) {
      date = date.add(1, 'day');
      if (this.isDateAvailable(date)) return date;
    }
    return null;
  }

  calculateTimeRemaining() {
    const now = this.getCurrentDate();
    const cutoff = now.hour(this.cutoffTime[0]).minute(this.cutoffTime[1]);
    const timeDiff = cutoff.diff(now, 'minute');

    return {
      hours: Math.floor(timeDiff / 60),
      minutes: timeDiff % 60,
      isActive: timeDiff > 0,
    };
  }

  handleFridayCase(now) {
    const cutoff = now.hour(this.cutoffTime[0]).minute(this.cutoffTime[1]);
    const nextMonday = this.getNextAvailableDate(now.day(5));
    const nextTuesday = this.getNextAvailableDate(now.day(6));

    if (now.isBefore(cutoff)) {
      const timeRemaining = this.calculateTimeRemaining();
      let timeString = `${timeRemaining.hours}hrs ${timeRemaining.minutes}mins`;
      if (timeRemaining.hours <= 0) {
        timeString = `${timeRemaining.minutes}mins`;
      }
      return {
        message: `<strong>Express Delivery</strong> on Monday if you order within`,
        timeStr: timeString,
        showTimer: true,
      };
    }
    return {
      message: `<strong>Express Delivery</strong> on Tuesday if you order by Monday at <strong class="el-delivery__countdown">${this.config.cutoff}</strong>`,
      showTimer: false,
    };
  }

  handleWeekendCase() {
    return {
      message: `<strong>Express Delivery</strong> on Tuesday if you order by Monday at <strong class="el-delivery__countdown">${this.config.cutoff}</strong>`,
      showTimer: false,
    };
  }

  updateMessage() {
    try {
      const now = this.getCurrentDate();
      let message = '';
      let showTimer = false;
      let timeStr = '';

      // Saturday or Sunday
      if (now.day() === 6 || now.day() === 0) {
        const weekendCase = this.handleWeekendCase();
        message = weekendCase.message;
        showTimer = weekendCase.showTimer;
      }
      // Friday
      else if (now.day() === 5) {
        const fridayCase = this.handleFridayCase(now);
        message = fridayCase.message;
        timeStr = fridayCase.timeStr || '';
        showTimer = fridayCase.showTimer;
      }
      // Other days
      else {
        const timeRemaining = this.calculateTimeRemaining();
        const tomorrow = now.add(1, 'day').startOf('day');
        const afterTomorrow = now.add(2, 'day').startOf('day');
        const deliveryDate = this.getNextAvailableDate(now);
        const daysDiff = deliveryDate?.diff(now, 'day') || 0;

        if (!deliveryDate || daysDiff > 5) {
          message = `Our warehouse will reopen on ${deliveryDate.format('Do MMMM YYYY')}`;
        } else if (timeRemaining.isActive && this.isDateAvailable(tomorrow)) {
          message = '<strong>Express Delivery</strong> tomorrow if you order within';
          const pluralSuffix = timeRemaining.hours > 1 ? 's' : '';
          let hoursRemainingString = timeRemaining.hours + pluralSuffix;
          if (timeRemaining.hours <= 0) {
            hoursRemainingString = '';
          }
          timeStr = `${hoursRemainingString} ${timeRemaining.minutes}mins`;
          showTimer = true;
        } else if (!timeRemaining.isActive && afterTomorrow.day() < 6) {
          message = `<strong>Express Delivery</strong> on ${deliveryDate.add(1, 'day').format('dddd')} if you order tomorrow by midday`;
        } else {
          message = `<strong>Express Delivery</strong> on Monday if you order Friday by ${this.config.cutoff}`;
        }
      }

      this.$message.html(showTimer ? `${message} <strong class="el-delivery__countdown">${timeStr}</strong>` : message);
      this.updateCountdown(showTimer);
    } catch (error) {
      console.error('Update error:', error);
    }
  }

  updateCountdown(show) {
    show ? this.$countdown.show() : this.$countdown.hide();
  }

  startAutoUpdate() {
    if (!this.testMode) {
      setInterval(() => this.updateMessage(), 60000);
    }
  }
};

// Init
elabs.onJavascriptReady(function () {
  $('[data-delivery-calculator]').each(function (index, element) {
    const pdpDeliveryCalculator = new elabs.DeliveryCalculator(element);
    window.pdpDeliveryCalculator = pdpDeliveryCalculator;
  });
});
